import { Injectable } from '@angular/core';
import { API_ENDPOINT } from '@core/configs';
import { ApiResponse } from '@core/models/interfaces';
import { Observable } from 'rxjs';
import { ApiService } from '../common';

export interface DeviceApiItem {
  deviceId: string;
  deviceName?: string | null;
  deviceType: 'TV' | 'PC';
  isOnline: boolean;
  corpOffice: string;
  userAgent?: string | null;
  appVersion?: string | null;
  webViewVersion?: string | null;
  insertDate?: string | null;
  updateDate?: string | null;
  lastHeartbeatAt?: string | null;
  settingConfig?: string | null;
  hasLicense: boolean;
  logEnabled: boolean;
}

/** Result of CareMoni device-licenses/update-log-enabled-by-device-ids and office-logs/set-by-devices */
export interface LogEnabledUpdateResponse {
  logEnabled: number;
  /** Firebase tokens that received the command (online) */
  updatedDevices: string[];
  /** Firebase tokens whose command write failed */
  failedDevices: string[];
  /** Firebase tokens of offline devices */
  offlineDevices: string[];
  /** Online devices that received the command and save logEnabled themselves */
  notifiedDeviceIds: string[];
  /** Devices saved by the API (offline, or the command failed) */
  savedDeviceIds: string[];
  notFoundDeviceIds: string[];
}

/** Result of CareMoni firebase/check-version: latest versions from Firebase */
export interface CheckVersionResponse {
  /** Firebase node tvAppVersion */
  appVersion: string;
  /** Firebase node webVersion */
  webVersion: string;
}

/** Result of CareMoni firebase/reload-web-version-by-device-tokens */
export interface ReloadWebVersionResponse {
  /** Tokens that received the Reload command */
  updatedDevices: string[];
  /** Tokens whose command write still failed after the retries */
  failedDevices: string[];
  /** Tokens with no node in Firebase deviceSessions */
  offlineDevices: string[];
}

export interface DeviceAppLogSummary {
  apiRequests: number;
  apiSuccess: number;
  apiErrors: number;
  retries: number;
  firebaseEvents: number;
  avgLatencySeconds?: number | null;
  maxLatencySeconds?: number | null;
  latestMemory?: DeviceAppLogMemorySample | null;
  weakNetworkLines: number;
  firebaseCadence: DeviceAppLogCadence;
  heartbeatCadence: DeviceAppLogCadence;
  /** Periods the app was running (no silence longer than 11 minutes) */
  runningPeriods: DeviceAppLogPeriod[];
}

export interface DeviceAppLogPeriod {
  /** JST, yyyy/MM/dd HH:mm:ss */
  from: string;
  /** JST, yyyy/MM/dd HH:mm:ss */
  to: string;
}

export interface DeviceAppLogCadenceEvent {
  /** JST, yyyy/MM/dd HH:mm:ss */
  time: string;
  isDelayed: boolean;
}

export interface DeviceAppLogCadence {
  observedCount: number;
  missingCount: number;
  delayedCount: number;
  gaps: DeviceAppLogCadenceGap[];
  events: DeviceAppLogCadenceEvent[];
  /** Expected times (JST) of the missing runs */
  missingSlots: string[];
}

export interface DeviceAppLogCadenceGap {
  from: string;
  to: string;
  minutes: number;
  missingCount: number;
  firstMissingAt?: string | null;
  lastMissingAt?: string | null;
  /** Starts at the start of a running period (app start / logging resumed) */
  isLeading: boolean;
  /** Ends at the last app log before the app stopped */
  isTrailing: boolean;
}

export interface DeviceAppLogMemorySample {
  systemUsedMb: number;
  systemTotalMb: number;
  appMb: number;
  appPercent: number;
}

export interface DeviceAppLogEndpointError {
  target: string;
  total: number;
  statusCounts: Record<string, number>;
  lastErrorAt: string;
}

export interface DeviceAppLogSlowRequest {
  time: string;
  target: string;
  status: string;
  latencySeconds: number;
}

export interface DeviceAppLogLine {
  /** JST, yyyy/MM/dd HH:mm:ss */
  time: string;
  userId: string;
  category: string;
  requestId: string;
  target: string;
  status: string;
  result: string;
  isError: boolean;
  latencySeconds?: number | null;
  systemMemoryPercent?: number | null;
  systemMemoryUsedMb?: number | null;
  systemMemoryTotalMb?: number | null;
  appMemoryPercent?: number | null;
  appMemoryMb?: number | null;
  networkPeak?: string | null;
  networkQuality?: string | null;
  networkType?: string | null;
  raw: string;
}

export interface DeviceAppLogResult {
  /** JST, yyyy/MM/dd HH:mm:ss */
  from: string;
  /** JST, yyyy/MM/dd HH:mm:ss */
  to: string;
  fileCount: number;
  totalLines: number;
  unparsedLines: number;
  truncated: boolean;
  summary: DeviceAppLogSummary;
  errorsByEndpoint: DeviceAppLogEndpointError[];
  slowRequests: DeviceAppLogSlowRequest[];
  lines: DeviceAppLogLine[];
}

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  constructor(private readonly http: ApiService) {}

  getAllDevices(): Observable<ApiResponse<DeviceApiItem[]>> {
    return this.http.get<DeviceApiItem[]>(API_ENDPOINT.authConsole.getAllDevices);
  }

  /** Update logEnabled of several devices (online or offline) in one request */
  updateLogEnabled(deviceIds: string[], logEnabled: boolean): Observable<ApiResponse<LogEnabledUpdateResponse>> {
    return this.http.post<LogEnabledUpdateResponse>(API_ENDPOINT.authConsole.updateDeviceLogEnabled, {
      deviceIds,
      logEnabled: logEnabled ? 1 : 0,
    });
  }

  /** Save logEnabled on the office and update the given devices of that office */
  setOfficeLogEnabled(
    corpPrefix: string,
    officeCd: string,
    logEnabled: boolean,
    deviceIds: string[],
  ): Observable<ApiResponse<LogEnabledUpdateResponse>> {
    return this.http.post<LogEnabledUpdateResponse>(API_ENDPOINT.authConsole.setOfficeLogEnabled, {
      corpPrefix,
      officeCd,
      logEnabled: logEnabled ? 1 : 0,
      deviceIds,
    });
  }

  /** Latest TV app version and web version from Firebase */
  checkVersion(): Observable<ApiResponse<CheckVersionResponse>> {
    return this.http.get<CheckVersionResponse>(API_ENDPOINT.authConsole.checkVersion);
  }

  /** Reload the given online TVs so they load the latest web version. Token: {corpPrefix}|{officeCd}|tv|{deviceId} */
  reloadWebVersionByDeviceTokens(deviceTokens: string[]): Observable<ApiResponse<ReloadWebVersionResponse>> {
    return this.http.post<ReloadWebVersionResponse>(API_ENDPOINT.authConsole.reloadWebVersionByDeviceTokens, {
      deviceTokens,
    });
  }

  /** from/to: JST, yyyy-MM-ddTHH:mm */
  getAppLogs(deviceId: string, from: string, to: string): Observable<ApiResponse<DeviceAppLogResult>> {
    return this.http.getWithQuery<DeviceAppLogResult>(API_ENDPOINT.authConsole.getDeviceAppLogs, {
      deviceId,
      from,
      to,
    });
  }
}
